import { useSessionAuth } from "@/auth/use-session-auth";
import { useEffect, useMemo, useState } from "react";
import useWebSocket, { Options, ReadyState } from "react-use-websocket";
import { v7 } from "uuid";
import { signalUrl } from "../../config.json";
import {
  App,
  IncomingMessage,
  Signal,
  SubscribeMessage,
  UnsubscribeMessage,
} from "./message";
import { getEnvSettings } from "../lib/utils";

const appIds = {
  [App.Task]: v7(),
  [App.AppU4DM]: v7(),
};

export function useSignalWS(app: App, options?: Options) {
  const [lastError, setLastError] = useState<Event | undefined>();
  const url = useMemo(() => signalURL(app), [app]);
  const auth = useSessionAuth();
  const socket = useWebSocket<IncomingMessage | null>(
    url,
    {
      ...options,
      share: true,
      skipAssert: true,
      retryOnError: true,
      protocols: ["v1s.signals.unified"],
      reconnectAttempts: Infinity,
      reconnectInterval: 5000,
      onError: (error) => setLastError(error),
      onOpen: () => setLastError(undefined),
      shouldReconnect: (closeEvent) => closeEvent.code !== 1000,
    },
    auth.ok
  );

  return { ...socket, lastError };
}

export function useSignal<M>(opts: Signal<M>, func: (message: M) => void) {
  const { app, channel, filters, enabled = true } = opts;

  const [sid, setSid] = useState<number | null>(null);
  const { readyState, sendJsonMessage } = useSignalWS(app, {
    onMessage: (e) => {
      if (!enabled) return;

      const msg = JSON.parse(e.data);
      const msgSid = msg.SID ?? msg.sid;
      const isReceiver = sid !== null && msgSid === sid;
      const isChannel = msg.FunctionalBlockClass === channel;
      const isFilter = msg.Filter === filters;

      if (!isReceiver && (!isChannel || !isFilter)) {
        return;
      }

      if (msg.M === "SubscribeAck") {
        setSid(msgSid);
        return;
      }

      if (msg.M === "UnsubscribeAck") {
        setSid(null);
        return;
      }

      if (msgSid === sid) {
        func(msg as M);
      }
    },
  });

  const isReady = readyState === ReadyState.OPEN;

  // Subscribe when ready
  useEffect(() => {
    if (!isReady) return;
    if (!enabled) return;

    sendJsonMessage<SubscribeMessage>({
      M: "Subscribe",
      filter: filters,
      functionalBlockClass: channel as string,
    });
  }, [isReady, enabled, filters, channel]);

  // Unsubscribe on unmount
  useEffect(() => {
    const SID = sid;
    if (SID === null) return;
    if (!enabled) return;

    return () => {
      sendJsonMessage<UnsubscribeMessage>({ M: "Unsubscribe", SID });
    };
  }, [sid, enabled]);

  return {
    app: opts.app,
    channel: opts.channel,
    acknowledged: isReady && sid !== null,
  } as const;
}

function signalURL(app: App) {
  const { baseUrl, sitHost } = getEnvSettings();
  const url = new URL(baseUrl);
  return signalUrl
    .replace(
      "<BASE>",
      url.origin.replace("https://", "wss://").replace("http://", "ws://")
    )
    .replace("<APP>", app)
    .replace("<SITHOST>", sitHost)
    .replace("<GUID>", appIds[app]);
}
